import React, { useContext, useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { MediaGalleryContext, MetadataContext, Modal, Tooltip, UserContext, SystemFeaturesContext } from '@/components';
import { ImageGroupeModel, ImageModel, ModuleBaseModel, ModuleBasePropsModel } from '@/types';
import { useTranslations } from 'next-intl';
import XLight from '@/assets/xLight.svg';
import MediaGalleryLeftSide from './MediaGalleryLeftSide';
import MediaGalleryRightSide, { MediaGalleryRightSideMethods } from './MediaGalleryRightSide';
import DownloadSimpleLight from '@/assets/downloadSimpleLight.svg';
import SelectionSlashLight from '@/assets/selectionSlashLight.svg';
import ScissorsLight from '@/assets/scissors-light.svg';
import { downloadImages, saveBulkToScrapbook } from '@/api';
import MediaGalleryCarouselPaginator from './MediaGalleryCarouselPaginator';
import FolderSelectionDialog from '@/components/scrapbook/dialogs/FolderSelectionDialog';
import classNames from 'classnames';
import { useNotification } from '@/components/general/NotificationProvider';

export type MediaGalleryImageModel = {
  index: number;
} & ImageModel;

type MediaGalleryImagesListModel = ModuleBaseModel<
  {
    groupedImages?: ImageGroupeModel[];
  } & ModuleBasePropsModel
> & {
  legacyImageList?: ImageModel[];
};

const MediaGalleryImagesList = (data: MediaGalleryImagesListModel) => {
  const {
    props: { groupedImages },
    legacyImageList,
  } = data;

  const [activeImageIndex, setActiveImageIndex] = useState<number>(0);
  const [presentingImageList, setPresentingImageList] = useState<MediaGalleryImageModel[]>([]);
  const [selectedImages, setSelectedImages] = useState<MediaGalleryImageModel[]>([]);
  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const t = useTranslations();
  const { open, selectedImage, closeMediaGallery, setShowGalleryButton } = useContext(MediaGalleryContext);
  const rightSide = useRef<MediaGalleryRightSideMethods>(null);
  const { pageType, pageId, pageUrl, pageTitle } = useContext(MetadataContext);
  const user = useContext(UserContext);
  const systemFeatures = useContext(SystemFeaturesContext);
  const { showNotification } = useNotification();

  // Memoize image list building to avoid expensive recalculations
  const presentingImageListMemo = useMemo(() => {
    const imageList: MediaGalleryImageModel[] = [];
    if (groupedImages && groupedImages.length > 0) {
      let index = 0;
      groupedImages.forEach((group) => {
        group.images.forEach((image) => {
          imageList.push({ ...image, index: index++ });
        });
      });
    }

    return imageList;
  }, [groupedImages, legacyImageList]);

  useEffect(() => {
    setPresentingImageList(presentingImageListMemo);
    setShowGalleryButton(presentingImageListMemo.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presentingImageListMemo]);

  useEffect(() => {
    if (selectedImage) {
      const selectedImageIndex = presentingImageList.findIndex((i) => i.mediaId === selectedImage.mediaId);
      if (selectedImageIndex > -1) {
        setActiveImageIndex(selectedImageIndex);
      } else {
        setActiveImageIndex(0);
        setPresentingImageList([{ ...selectedImage, index: 0 }]);
      }
    } else {
      setActiveImageIndex(0);
    }
  }, [selectedImage, presentingImageList]);

  const setActiveImage = useCallback((image: MediaGalleryImageModel) => {
    setActiveImageIndex(image.index);
  }, []);

  const selectImage = useCallback((image: MediaGalleryImageModel) => {
    setSelectedImages((prev) => {
      if (prev.some((item) => item.mediaId === image.mediaId)) {
        return prev.filter((item) => item.mediaId !== image.mediaId);
      } else {
        return [...prev, image];
      }
    });
  }, []);

  const downloadAllSelectedImages = useCallback(() => {
    const imagesList = selectedImages.length === 0 ? presentingImageList : selectedImages;
    const objectNumberList = imagesList.map((image) => image.mediaId);
    downloadImages(objectNumberList);
  }, [selectedImages, presentingImageList]);

  const downloadAllBtn =
    (pageType === 'legacyReportPage' || pageType === 'reportPage') && (user.isAuthenticated || user.isIpBasedIdentity);

  const showSaveToScrapbook =
    systemFeatures.saveToScrapbook &&
    (pageType === 'legacyReportPage' || pageType === 'reportPage') &&
    user.isAuthenticated;

  const createSaveScrapbookObject = (image: MediaGalleryImageModel) => {
    const content = {
      imageId: image.mediaId,
      imageUrl: image.imageUrl,
      imageCaption: image.caption,
      imageCopyright: image.copyrightText,
      pageId: pageId,
      pageUrl: pageUrl,
      pageTitle: pageTitle,
    };
    return {
      path: `img.${image.mediaId}.json`,
      contentType: 'application/x.stylus.lnk.img+json',
      content: JSON.stringify(content),
    };
  };

  const handleSaveToScrapbook = async (folderId: number) => {
    const imagesList = selectedImages.length === 0 ? presentingImageList : selectedImages;

    try {
      const contentList = imagesList.map((image) => createSaveScrapbookObject(image));
      const result = await saveBulkToScrapbook(folderId, contentList, user.userId);

      if (result?.succeeded) {
        showNotification(result.message || 'Images saved to scrapbook successfully', 'savesuccess');
        setSelectedImages([]);
      } else {
        showNotification('Call to scrapbook server failed.', 'error');
      }
    } catch (error) {
      showNotification('Call to scrapbook server failed.', 'error');
      throw error;
    }
  };

  return (
    <Modal
      open={open}
      onClose={closeMediaGallery}
      contanerClassName="w-full h-full lg:h-[90%] lg:w-[95%] xl:w-[80%] bg-white !rounded-none lg:!rounded-md "
    >
      <div className="flex h-full w-full flex-col">
        <div className="flex items-center justify-between px-3 py-2 lg:px-6 lg:py-4">
          <div className="w-7" />
          <div className="text-xl font-medium">{t('general.$gallery')}</div>
          <button onClick={closeMediaGallery} className="btn icon">
            <XLight className="h-7 w-7" />
          </button>
        </div>
        <div className="flex grow-1 flex-col-reverse overflow-hidden border-y border-black/10 sm:px-4 lg:flex-row">
          {presentingImageList.length > 1 && (
            <div className="left-side h-full overflow-y-scroll lg:w-1/4" data-lenis-prevent>
              <MediaGalleryLeftSide
                imageList={presentingImageList}
                activeImageIndex={activeImageIndex}
                selectedImages={selectedImages}
                selectImage={selectImage}
                setActiveImage={setActiveImage}
                showSaveToScrapbook={showSaveToScrapbook}
                showDownloadBtn={downloadAllBtn}
              />
            </div>
          )}
          <div
            className={classNames(
              'right-side px-2 lg:h-full lg:px-0',
              presentingImageList.length > 1 ? 'h-3/5 lg:w-3/4' : 'h-full lg:w-full',
            )}
          >
            <MediaGalleryRightSide
              ref={rightSide}
              imageList={presentingImageList}
              activeImageIndex={activeImageIndex}
              setActiveImage={setActiveImage}
            />
          </div>
        </div>
        <div className="flex py-2 pl-7">
          <div className="flex w-full items-center gap-3 py-2 lg:w-1/4">
            {selectedImages.length > 0 && (
              <Tooltip content={t('imageGallery.$clearSelection')} placement="top">
                <button className="btn secondary icon !border-black !px-7 !py-2" onClick={() => setSelectedImages([])}>
                  <SelectionSlashLight className="h-6 w-6 !fill-black" />
                </button>
              </Tooltip>
            )}
            {showSaveToScrapbook && (
              <button
                className="btn secondary !border-black !px-7 !py-2 !text-sm !font-medium whitespace-nowrap"
                onClick={() => setShowFolderDialog(true)}
              >
                <ScissorsLight className="h-6 w-6 !fill-black" />
                <span className="hidden lg:block">
                  {'Save to Scrapbook' + (selectedImages.length > 0 ? ` (${selectedImages.length})` : '')}
                </span>
              </button>
            )}
            {downloadAllBtn && (
              <button
                className="btn secondary !border-black !px-7 !py-2 !text-sm !font-medium whitespace-nowrap"
                onClick={downloadAllSelectedImages}
              >
                <DownloadSimpleLight className="h-6 w-6 !fill-black" />
                <span className="hidden lg:block">
                  {t('general.$downloadAll') + (selectedImages.length > 0 ? ` (${selectedImages.length})` : '')}
                </span>
              </button>
            )}
          </div>
          <div className="pointer-events-none hidden items-center justify-center lg:flex lg:w-3/4">
            {presentingImageList.length > 1 && (
              <MediaGalleryCarouselPaginator
                imageList={presentingImageList}
                activeImageIndex={activeImageIndex}
                carouselPrev={() => rightSide.current?.carouselPrev()}
                carouselNext={() => rightSide.current?.carouselNext()}
              />
            )}
          </div>
        </div>
      </div>

      <FolderSelectionDialog
        isOpen={showFolderDialog}
        onClose={() => setShowFolderDialog(false)}
        onSave={handleSaveToScrapbook}
        reportTitle={pageTitle || 'Gallery Images'}
      />
    </Modal>
  );
};

export default MediaGalleryImagesList;
